import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export function ChatProvider({ children }) {
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  // Distinct from chats.length > 0: a tenant with zero chats is still
  // "loaded" and must not keep re-triggering loadChats() or leave pages
  // stuck on a skeleton forever (RPAA-213 backoffice review finding #1).
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const { enteredTenant } = useAuth();

  // Generation counter guarding against a stale, already in-flight
  // loadChats() call landing (and re-populating state) after the tenant has
  // changed (RPAA-213 finding #2). Every loadChats() call, and the tenant
  // reset effect below, bumps this counter; a response is only applied if
  // the counter hasn't moved since the call started.
  const loadGeneration = useRef(0);

  // Reset chat state whenever the entered tenant changes (enter, exit, or
  // switch from one tenant to another) so stale chats/selectedChatId from a
  // previous tenant never leak into a new tenant's requests (RPAA-213).
  // chats.length === 0 afterwards, so the load-guard used across pages
  // (`if (!isPlatformOnlySuperAdmin() && chats.length === 0 && !chatsLoading) loadChats()`)
  // fires again for the newly entered/exited tenant.
  useEffect(() => {
    // Invalidate any loadChats() call already in flight from before this
    // tenant change so its response can't repopulate the state we're about
    // to clear.
    loadGeneration.current += 1;
    setChats([]);
    setSelectedChatId(null);
    setChatsLoaded(false);
    // Also clear loading directly (rather than relying on a stale call's
    // now-gated `finally` to do it — see loadChats below) so the page guard
    // can fire cleanly for the new tenant immediately, without waiting on
    // anything in flight.
    setLoading(false);
  }, [enteredTenant?.id]);

  // Don't auto-load chats on mount
  // Chats are loaded:
  // - For regular users: by their pages (configs, files, etc)
  // - For super admin: by TenantManagement inline drill-down
  // This avoids unnecessary calls without tenant_id

  const loadChats = async (accountId = null, tenantId = null) => {
    loadGeneration.current += 1;
    const myGeneration = loadGeneration.current;
    try {
      setLoading(true);
      const { getChats } = await import('../services/chatApi');

      // Pass account_id (preferred) or tenant_id (fallback)
      const response = await getChats(accountId, tenantId);

      if (myGeneration !== loadGeneration.current) {
        // Tenant changed (or a newer loadChats call superseded this one)
        // while this request was in flight — discard the stale response
        // instead of applying it.
        return;
      }

      if (response.data && response.data.chats) {
        // Sort chats by chatwoot_inbox_id (ascending)
        const sortedChats = [...response.data.chats].sort((a, b) =>
          (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0)
        );

        setChats(sortedChats);

        // Auto-select chat with lowest inbox_id if none already selected.
        // Uses the functional updater so it always sees the latest
        // selectedChatId rather than the value captured in this closure
        // when loadChats() was first called (RPAA-213 finding #2).
        setSelectedChatId(prev => {
          if (prev || sortedChats.length === 0) {
            return prev;
          }
          // Get the first active chat (already sorted by inbox_id)
          const defaultChat = sortedChats.find(c => c.is_active) || sortedChats[0];
          return defaultChat.id;
        });
      }
    } catch (error) {
      if (myGeneration === loadGeneration.current) {
        console.error('Error loading chats:', error);
        setChats([]);
      }
    } finally {
      // Gated on generation too: an already-discarded (stale) call must not
      // flip loading back to false out from under a newer call that is
      // still in flight — otherwise the page guard
      // (`chats.length === 0 && !chatsLoading`) fires again prematurely and
      // issues yet another loadChats() call, which under React.StrictMode's
      // double-invoked effects on mount would repeat indefinitely (each
      // newly-fired call's stale predecessor keeps re-opening the guard).
      // The tenant-change reset effect clears loading directly instead, so
      // it doesn't depend on this stale finally to do it.
      if (myGeneration === loadGeneration.current) {
        setLoading(false);
        setChatsLoaded(true);
      }
    }
  };

  const getSelectedChat = () => {
    return chats.find(c => c.id === selectedChatId);
  };

  const value = {
    selectedChatId,
    setSelectedChatId,
    chats,
    loading,
    chatsLoaded,
    loadChats,
    getSelectedChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

